class CPilotBundleMaker extends HTMLElement {
  connectedCallback() {
    this.config = this.readConfig();
    if (!this.config || !this.config.products.length) return;

    this.instanceId = this.dataset.cpilotInstanceId;
    this.productFormId = this.dataset.cpilotProductFormId;
    this.currentProductHandle = this.dataset.cpilotProduct;
    this.builderEnabled = this.dataset.cpilotBuilderEnabled !== 'false';
    this.selectedSize = this.getCurrentVariant()?.size || this.findFirstAvailableVariant()?.size || null;
    this.mixItems = [];

    this.cacheElements();
    this.bindEvents();
    this.renderOfferState();
    this.renderDrawer();
    this.observeStickyTrigger();
  }

  disconnectedCallback() {
    this.stickyObserver?.disconnect();
    if (this.boundRenderFromCartChange) {
      document.removeEventListener('on:cart:after-merge', this.boundRenderFromCartChange);
      document.removeEventListener('on:cart:change', this.boundRenderFromCartChange);
    }
  }

  readConfig() {
    const script = this.querySelector('[data-cpilot-bundle-products]');
    if (!script) return null;

    try {
      const parsed = JSON.parse(script.textContent);
      const products = Array.isArray(parsed.products)
        ? parsed.products
            .filter((product) => product.available && Array.isArray(product.variants))
            .map((product) => ({
              ...product,
              variants: product.variants.map((variant) => ({
                ...variant,
                id: String(variant.id),
                price: Number(variant.price || 0),
                available: Boolean(variant.available)
              }))
            }))
            .filter((product) => product.variants.some((variant) => variant.available))
        : [];

      return { ...parsed, products };
    } catch (error) {
      return null;
    }
  }

  cacheElements() {
    this.productForm = this.productFormId ? document.getElementById(this.productFormId) : null;
    this.variantInput = this.productForm?.querySelector('[name="id"]');
    this.quantityInput = this.productForm?.querySelector('[name="quantity"]');
    this.atcButton = this.productForm?.querySelector('[name="add"]');
    this.atcSubline = document.querySelector(`[data-cpilot-atc-subline="${this.instanceId}"]`);
    this.externalOpenButtons = Array.from(document.querySelectorAll(`[data-cpilot-bundle-external-open="${this.instanceId}"]`));
    this.rungs = Array.from(this.querySelectorAll('[data-cpilot-tier-rung]'));
    this.tierPriceEls = Array.from(this.querySelectorAll('[data-cpilot-tier-price]'));
    this.priceCountEl = this.querySelector('[data-cpilot-price-count]');
    this.priceUnitEl = this.querySelector('[data-cpilot-price-unit]');
    this.priceCompareEl = this.querySelector('[data-cpilot-price-compare]');
    this.priceTotalEl = this.querySelector('[data-cpilot-price-total]');
    this.priceSavingEl = this.querySelector('[data-cpilot-price-saving]');
    this.liveEl = this.querySelector('[data-cpilot-bundle-live]');
    this.stickyBar = this.querySelector('[data-cpilot-sticky-bar]');
    this.stickyTotalEl = this.querySelector('[data-cpilot-sticky-total]');
    this.stickyMetaEl = this.querySelector('[data-cpilot-sticky-meta]');
    this.stickyNudgeEl = this.querySelector('[data-cpilot-sticky-nudge]');
    this.stickySubmit = this.querySelector('[data-cpilot-sticky-submit]');
    this.drawer = this.querySelector('[data-cpilot-bundle-drawer]');
    this.closeButtons = Array.from(this.querySelectorAll('[data-cpilot-bundle-close]'));
    this.coloursEl = this.querySelector('[data-cpilot-bundle-colours]');
    this.sizesEl = this.querySelector('[data-cpilot-bundle-sizes]');
    this.itemsEl = this.querySelector('[data-cpilot-bundle-items]');
    this.drawerSavingEl = this.querySelector('[data-cpilot-bundle-drawer-saving]');
    this.drawerCompareEl = this.querySelector('[data-cpilot-bundle-drawer-compare]');
    this.drawerTotalEl = this.querySelector('[data-cpilot-bundle-drawer-total]');
    this.drawerSavedEl = this.querySelector('[data-cpilot-bundle-drawer-saved]');
    this.drawerSubmitNoteEl = this.querySelector('[data-cpilot-bundle-submit-note]');
    this.submitButton = this.querySelector('[data-cpilot-bundle-submit]');
  }

  bindEvents() {
    this.quantityInput?.addEventListener('input', () => this.handleQuantityChange());
    this.quantityInput?.addEventListener('change', () => this.handleQuantityChange());

    this.productForm?.addEventListener('change', () => {
      window.setTimeout(() => {
        const currentVariant = this.getCurrentVariant();
        if (currentVariant) this.selectedSize = currentVariant.size || this.selectedSize;
        this.renderOfferState();
        this.renderDrawer();
      }, 0);
    });

    this.externalOpenButtons.forEach((button) => {
      button.addEventListener('click', () => this.openDrawer(button));
    });

    this.stickySubmit?.addEventListener('click', () => this.submitProductForm());
    this.closeButtons.forEach((button) => button.addEventListener('click', () => this.closeDrawer()));

    this.drawer?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeDrawer();
    });

    this.sizesEl?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-cpilot-bundle-size]');
      if (!button || button.getAttribute('aria-disabled') === 'true') return;
      this.selectedSize = button.dataset.cpilotBundleSize;
      this.renderDrawer();
    });

    this.coloursEl?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-cpilot-bundle-colour]');
      if (!button || button.getAttribute('aria-disabled') === 'true') return;
      this.addVariantToMix(button.dataset.cpilotBundleVariant);
    });

    this.itemsEl?.addEventListener('click', (event) => {
      const removeButton = event.target.closest('[data-cpilot-bundle-remove]');
      const quantityButton = event.target.closest('[data-cpilot-bundle-adjust]');
      if (removeButton) {
        this.removeMixItem(removeButton.dataset.cpilotBundleRemove);
      } else if (quantityButton) {
        this.adjustMixItem(quantityButton.dataset.cpilotBundleAdjust, Number(quantityButton.dataset.cpilotBundleDelta || 0));
      }
    });

    this.submitButton?.addEventListener('click', () => this.addMixToCart());

    this.boundRenderFromCartChange = () => {
      window.setTimeout(() => this.renderOfferState(), 0);
    };
    document.addEventListener('on:cart:after-merge', this.boundRenderFromCartChange);
    document.addEventListener('on:cart:change', this.boundRenderFromCartChange);
  }

  handleQuantityChange() {
    if (!this.quantityInput) return;
    const value = this.getNativeQuantity();
    const clamped = Math.min(Math.max(value, 1), 12);
    if (String(clamped) !== String(this.quantityInput.value)) {
      this.quantityInput.value = String(clamped);
    }
    this.renderOfferState();
  }

  renderOfferState() {
    const quantity = this.getNativeQuantity();
    const variant = this.getCurrentVariant();
    const price = variant?.price || this.findFirstAvailableVariant()?.price || 0;
    const metrics = this.getBundleMetrics(quantity, price);
    const cartEligibleTeeCount = this.readCartEligibleTeeCount();

    this.renderTierPrices(price);
    this.renderRungs(quantity);
    this.renderPriceBlock(quantity, metrics);
    this.renderAtcContext(quantity, variant, metrics);
    this.renderSticky(quantity, variant, metrics, cartEligibleTeeCount);
  }

  renderTierPrices(price) {
    this.tierPriceEls.forEach((el) => {
      const tier = Number(el.dataset.cpilotTierPrice || 1);
      const rate = tier >= 5 ? 0.2 : tier >= 3 ? 0.1 : 0;
      el.textContent = this.formatMoney(Math.round(price * (1 - rate)));
    });
  }

  renderRungs(quantity) {
    this.rungs.forEach((rung) => {
      const tier = Number(rung.dataset.cpilotTierRung || 1);
      const active = (tier === 5 && quantity >= 5)
        || (tier === 3 && quantity >= 3 && quantity < 5)
        || (tier === 1 && quantity < 3);
      rung.classList.toggle('is-active', active);
      rung.setAttribute('aria-current', String(active));
    });
  }

  renderPriceBlock(quantity, metrics) {
    if (this.priceCountEl) this.priceCountEl.textContent = `${quantity} ${quantity === 1 ? 'tee' : 'tees'}`;
    if (this.priceUnitEl) this.priceUnitEl.textContent = this.formatMoney(metrics.unit);
    if (this.priceTotalEl) this.priceTotalEl.textContent = this.formatMoney(metrics.total);

    if (this.priceCompareEl) {
      this.priceCompareEl.hidden = metrics.discountRate === 0;
      this.priceCompareEl.textContent = this.formatMoney(metrics.subtotal);
    }

    if (this.priceSavingEl) {
      this.priceSavingEl.hidden = metrics.discountRate === 0;
      this.priceSavingEl.textContent = `Save ${this.formatMoney(metrics.saving)} (${metrics.discountPercent}% off)`;
    }
  }

  renderAtcContext(quantity, variant, metrics) {
    if (this.atcButton) {
      if (!variant?.available) {
        this.atcButton.textContent = 'Choose an available size';
      } else {
        this.atcButton.textContent = `Add to bag · ${this.formatMoney(metrics.total)}`;
      }
    }

    if (!this.atcSubline) return;

    if (!variant?.available) {
      this.atcSubline.textContent = '';
    } else if (quantity < 3) {
      const remaining = 3 - quantity;
      this.atcSubline.textContent = `+${remaining} more saves 10% on the bundle`;
    } else if (quantity < 5) {
      this.atcSubline.textContent = '10% off · applied automatically at checkout';
    } else {
      this.atcSubline.textContent = '20% off · applied automatically at checkout';
    }

    if (this.liveEl) this.liveEl.textContent = this.atcSubline.textContent;
  }

  renderSticky(quantity, variant, metrics, cartEligibleTeeCount) {
    if (!this.stickyBar) return;

    if (this.stickyTotalEl) {
      this.stickyTotalEl.innerHTML = metrics.discountRate
        ? `<s>${this.formatMoney(metrics.subtotal)}</s> ${this.formatMoney(metrics.total)}`
        : this.formatMoney(metrics.total);
    }

    if (this.stickyMetaEl) {
      this.stickyMetaEl.textContent = metrics.discountRate
        ? `Save ${this.formatMoney(metrics.saving)} (${metrics.discountPercent}% off)`
        : `${this.formatMoney(metrics.unit)} per tee`;
    }

    if (this.stickyNudgeEl) {
      this.stickyNudgeEl.textContent = variant?.available
        ? this.getStickyTierNudge(quantity, cartEligibleTeeCount)
        : 'Choose an available size';
    }

    if (this.stickySubmit) {
      this.stickySubmit.disabled = !variant?.available;
      this.stickySubmit.textContent = variant?.available ? this.getStickySubmitLabel(quantity, cartEligibleTeeCount) : 'Choose size';
    }
  }

  getStickyTierNudge(quantity, cartEligibleTeeCount = 0) {
    const cartCount = Math.max(Number(cartEligibleTeeCount || 0), 0);
    const projectedCount = this.getProjectedEligibleTeeCount(quantity, cartCount);

    if (cartCount >= 5) {
      return 'Earning 20% bundle saving';
    }

    if (cartCount >= 3) {
      if (projectedCount >= 5) return 'This add reaches 20% bundle saving';
      const remaining = 5 - cartCount;
      return `Add ${remaining} more to save 20%`;
    }

    if (cartCount > 0) {
      if (projectedCount >= 3) return 'This add reaches 10% bundle saving';
      const remaining = 3 - cartCount;
      return `Add ${remaining} more to save 10%`;
    }

    if (projectedCount >= 5) return 'This add reaches 20% bundle saving';
    if (projectedCount >= 3) return 'This add reaches 10% bundle saving';

    const remaining = 3 - projectedCount;
    return `Add ${remaining} more to save 10%`;
  }

  getStickySubmitLabel(quantity, cartEligibleTeeCount = 0) {
    const quantityLabel = quantity === 1 ? 'Add to bag' : `Add ${quantity} to bag`;
    const projectedCount = this.getProjectedEligibleTeeCount(quantity, cartEligibleTeeCount);

    if (projectedCount >= 5) return `${quantityLabel} - earn 20%`;
    if (projectedCount >= 3) return `${quantityLabel} - earn 10%`;
    return quantityLabel;
  }

  getProjectedEligibleTeeCount(quantity, cartEligibleTeeCount = 0) {
    return Math.max(Number(cartEligibleTeeCount || 0), 0) + Math.max(Number(quantity || 0), 0);
  }

  renderDrawer() {
    if (!this.builderEnabled || !this.drawer) return;

    this.renderSizeOptions();
    this.renderColourOptions();
    this.renderMixItems();
    this.updateDrawerFooter();
  }

  renderSizeOptions() {
    if (!this.sizesEl) return;
    const sizes = this.getAllSizes();
    if (!sizes.includes(this.selectedSize)) this.selectedSize = sizes[0] || null;

    this.sizesEl.innerHTML = sizes.map((size) => {
      const available = this.config.products.some((product) =>
        product.variants.some((variant) => variant.size === size && variant.available)
      );
      const selected = size === this.selectedSize;
      return `
        <button
          type="button"
          class="cpilot-bundle-drawer__size${selected ? ' is-active' : ''}"
          data-cpilot-bundle-size="${this.escapeHtml(size)}"
          aria-pressed="${String(selected)}"
          aria-disabled="${String(!available)}"
        >
          ${this.escapeHtml(size)}
        </button>
      `;
    }).join('');
  }

  renderColourOptions() {
    if (!this.coloursEl) return;

    this.coloursEl.innerHTML = this.config.products.map((product) => {
      const variant = product.variants.find((candidate) => candidate.size === this.selectedSize);
      const available = Boolean(variant?.available);
      const image = product.imageUrl
        ? `<span class="cpilot-bundle-drawer__colour-thumb"><img src="${this.escapeHtml(product.imageUrl)}" alt=""></span>`
        : '<span class="cpilot-bundle-drawer__colour-thumb" aria-hidden="true"></span>';

      return `
        <button
          type="button"
          class="cpilot-bundle-drawer__colour"
          data-cpilot-bundle-colour="${this.escapeHtml(product.handle)}"
          data-cpilot-bundle-variant="${this.escapeHtml(variant?.id || '')}"
          aria-disabled="${String(!available)}"
        >
          ${image}
          <span>${this.escapeHtml(product.colourLabel)}</span>
        </button>
      `;
    }).join('');
  }

  renderMixItems() {
    if (!this.itemsEl) return;

    if (!this.mixItems.length) {
      this.itemsEl.innerHTML = '<p class="cpilot-bundle-drawer__empty">Pick any 3+ Classic Fit tees · save 10% · 5+ saves 20%</p>';
      return;
    }

    this.itemsEl.innerHTML = this.mixItems.map((item) => `
      <div class="cpilot-bundle-drawer__item">
        <span class="cpilot-bundle-drawer__item-thumb">
          ${item.imageUrl ? `<img src="${this.escapeHtml(item.imageUrl)}" alt="">` : ''}
        </span>
        <span class="cpilot-bundle-drawer__item-copy">
          <strong>${this.escapeHtml(item.colourLabel)} ${this.escapeHtml(item.size)}</strong>
          <small>${item.quantity > 1 ? `${item.quantity} tees` : '1 tee'}</small>
        </span>
        <span class="cpilot-bundle-drawer__item-qty" aria-label="Quantity">
          <button type="button" data-cpilot-bundle-adjust="${this.escapeHtml(item.variantId)}" data-cpilot-bundle-delta="-1">-</button>
          <span>${item.quantity}</span>
          <button type="button" data-cpilot-bundle-adjust="${this.escapeHtml(item.variantId)}" data-cpilot-bundle-delta="1">+</button>
        </span>
        <button type="button" class="cpilot-bundle-drawer__item-remove" data-cpilot-bundle-remove="${this.escapeHtml(item.variantId)}">
          Remove
        </button>
      </div>
    `).join('');
  }

  updateDrawerFooter() {
    const count = this.getMixCount();
    const subtotal = this.mixItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const metrics = this.getBundleMetrics(count, count ? Math.round(subtotal / count) : this.findFirstAvailableVariant()?.price || 0, subtotal);

    if (this.drawerSavingEl) {
      if (count === 0) {
        this.drawerSavingEl.textContent = 'Pick any 3+ Classic Fit tees · save 10% · 5+ saves 20%';
      } else if (count < 3) {
        const remaining = 3 - count;
        this.drawerSavingEl.textContent = `+${remaining} more saves 10% on the bundle`;
      } else if (count < 5) {
        this.drawerSavingEl.textContent = '10% off · applied automatically at checkout';
      } else {
        this.drawerSavingEl.textContent = '20% off · applied automatically at checkout';
      }
    }

    if (this.drawerTotalEl) this.drawerTotalEl.textContent = this.formatMoney(metrics.total);
    if (this.drawerCompareEl) {
      this.drawerCompareEl.hidden = metrics.discountRate === 0;
      this.drawerCompareEl.textContent = this.formatMoney(metrics.subtotal);
    }
    if (this.drawerSavedEl) {
      this.drawerSavedEl.hidden = metrics.discountRate === 0;
      this.drawerSavedEl.textContent = `Save ${this.formatMoney(metrics.saving)}`;
    }

    if (this.submitButton) {
      this.submitButton.disabled = count === 0;
      this.submitButton.textContent = count
        ? `Add ${count} ${count === 1 ? 'tee' : 'tees'} to bag · ${this.formatMoney(metrics.total)}`
        : 'Pick at least one tee';
    }

    if (this.drawerSubmitNoteEl) {
      if (count === 0) {
        this.drawerSubmitNoteEl.textContent = '';
      } else if (count < 3) {
        const remaining = 3 - count;
        this.drawerSubmitNoteEl.textContent = `+${remaining} more saves 10% on the bundle`;
      } else if (count < 5) {
        this.drawerSubmitNoteEl.textContent = '10% off · applied automatically at checkout';
      } else {
        this.drawerSubmitNoteEl.textContent = '20% off · applied automatically at checkout';
      }
    }
  }

  addVariantToMix(variantId) {
    const product = this.findProductByVariantId(variantId);
    const variant = product?.variants.find((candidate) => candidate.id === String(variantId));
    if (!product || !variant?.available) return;

    const existing = this.mixItems.find((item) => item.variantId === variant.id);
    if (existing) {
      existing.quantity += 1;
    } else {
      this.mixItems.push({
        variantId: variant.id,
        handle: product.handle,
        colourLabel: product.colourLabel,
        imageUrl: product.imageUrl,
        size: variant.size,
        price: variant.price,
        quantity: 1
      });
    }

    this.renderDrawer();
  }

  adjustMixItem(variantId, delta) {
    const item = this.mixItems.find((candidate) => candidate.variantId === variantId);
    if (!item) return;

    item.quantity += delta;
    if (item.quantity <= 0) {
      this.removeMixItem(variantId);
      return;
    }

    this.renderDrawer();
  }

  removeMixItem(variantId) {
    this.mixItems = this.mixItems.filter((item) => item.variantId !== variantId);
    this.renderDrawer();
  }

  async addMixToCart() {
    if (!this.mixItems.length || !this.submitButton) return;

    this.setDrawerBusy(true);

    try {
      const data = await this.postCartItems(this.mixItems.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity
      })));
      this.refreshCartSections(data);
      this.mixItems = [];
      this.renderDrawer();
      this.closeDrawer();
      this.openCartDrawer();
    } catch (error) {
      if (this.drawerSavingEl) {
        this.drawerSavingEl.textContent = 'Something changed in stock. Please review your picks and try again.';
      }
    } finally {
      this.setDrawerBusy(false);
    }
  }

  async postCartItems(items) {
    const sectionIds = this.getCartSectionIds();
    const response = await fetch(window.theme?.routes?.cartAdd || '/cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items,
        sections: sectionIds,
        sections_url: window.location.pathname
      })
    });
    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Cart add failed');
    }

    return data;
  }

  getCartSectionIds() {
    return ['page-header', 'cart-drawer']
      .map((selector) => document.querySelector(selector))
      .filter(Boolean)
      .map((section) => section.dataset.sectionId)
      .filter(Boolean);
  }

  refreshCartSections(data) {
    if (!data.sections) {
      this.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
      return;
    }

    ['page-header', 'cart-drawer'].forEach((selector) => {
      const section = document.querySelector(selector);
      const sectionId = section?.dataset?.sectionId;
      if (section && sectionId && data.sections[sectionId] && typeof section.updateFromCartChange === 'function') {
        section.updateFromCartChange(data.sections[sectionId]);
      }
    });

    this.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
  }

  openCartDrawer() {
    if (window.theme?.settings?.afterAddToCart !== 'drawer') return;
    const drawer = document.querySelector('.js-cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open(this.submitButton || this.externalOpenButtons[0]);
    }
  }

  openDrawer(trigger) {
    if (!this.drawer) return;
    this.lastTrigger = trigger || this.externalOpenButtons[0] || null;
    this.mixItems = [];
    this.renderDrawer();
    this.drawer.hidden = false;
    document.documentElement.classList.add('cpilot-bundle-drawer-open');
    window.setTimeout(() => {
      this.drawer.querySelector('.cpilot-bundle-drawer__close')?.focus({ preventScroll: true });
    }, 0);
  }

  closeDrawer() {
    if (!this.drawer) return;
    this.drawer.hidden = true;
    document.documentElement.classList.remove('cpilot-bundle-drawer-open');
    this.lastTrigger?.focus({ preventScroll: true });
  }

  setDrawerBusy(isBusy) {
    if (!this.submitButton) return;
    this.submitButton.disabled = isBusy || this.getMixCount() === 0;
    this.submitButton.classList.toggle('is-loading', isBusy);
  }

  submitProductForm() {
    if (typeof this.productForm?.requestSubmit === 'function') {
      this.productForm.requestSubmit(this.atcButton);
    } else {
      this.atcButton?.click();
    }
  }

  observeStickyTrigger() {
    if (!this.stickyBar || !this.atcButton || !('IntersectionObserver' in window)) return;

    this.stickyObserver = new IntersectionObserver((entries) => {
      const entry = entries[0];
      const show = entry && !entry.isIntersecting;
      this.stickyBar.classList.toggle('is-visible', Boolean(show));
      this.stickyBar.setAttribute('aria-hidden', show ? 'false' : 'true');
    }, { threshold: 0.02 });

    this.stickyObserver.observe(this.atcButton);
  }

  getBundleMetrics(quantity, unitPrice, explicitSubtotal) {
    const safeQuantity = Math.max(Number(quantity || 0), 0);
    const subtotal = explicitSubtotal ?? safeQuantity * Number(unitPrice || 0);
    const discountRate = safeQuantity >= 5 ? 0.2 : safeQuantity >= 3 ? 0.1 : 0;
    const saving = Math.round(subtotal * discountRate);
    const total = subtotal - saving;
    const unit = safeQuantity ? Math.round(total / safeQuantity) : Number(unitPrice || 0);
    return {
      quantity: safeQuantity,
      subtotal,
      discountRate,
      discountPercent: Math.round(discountRate * 100),
      saving,
      total,
      unit
    };
  }

  getNativeQuantity() {
    const value = Number(this.quantityInput?.value || 1);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  readCartEligibleTeeCount() {
    const cache = document.querySelector('[data-merge="cpilot-bundle-threshold"]')?.dataset?.mergeCache;
    const value = Number(String(cache || '').split('|')[0]);
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  getMixCount() {
    return this.mixItems.reduce((sum, item) => sum + item.quantity, 0);
  }

  getCurrentVariant() {
    const variantId = this.variantInput?.value || this.productForm?.querySelector('[name="id"]')?.value || this.config.currentVariantId;
    return this.findVariantById(variantId) || this.findFirstAvailableVariant();
  }

  getAllSizes() {
    const seen = new Set();
    const sizes = [];
    this.config.products.forEach((product) => {
      product.variants.forEach((variant) => {
        if (!seen.has(variant.size)) {
          seen.add(variant.size);
          sizes.push(variant.size);
        }
      });
    });
    return sizes;
  }

  findFirstAvailableVariant() {
    for (const product of this.config.products) {
      const variant = product.variants.find((candidate) => candidate.available);
      if (variant) return variant;
    }
    return null;
  }

  findVariantById(variantId) {
    for (const product of this.config.products) {
      const variant = product.variants.find((candidate) => String(candidate.id) === String(variantId));
      if (variant) return variant;
    }
    return null;
  }

  findProductByVariantId(variantId) {
    return this.config.products.find((product) => product.variants.some((variant) => String(variant.id) === String(variantId)));
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'GBP'
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `£${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  }

  escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}

if (!window.customElements.get('cpilot-bundle-maker')) {
  window.customElements.define('cpilot-bundle-maker', CPilotBundleMaker);
}
